import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import {
  FileInputValueModel,
  FileUploadErrorModel,
  FileUploadHandler,
  FileValidationErrorCollection,
  FileValidationErrorModel,
} from '@libs/form/models';
import { AppErrorModel } from '@libs/shared/models';
import { firstValueFrom } from 'rxjs';

/**
 * File Upload Validator
 *
 * ***IMPORTANT*** - The validator uploads files and ***SILENTLY UPDATES CONTROL VALUE*** by adding `fileIdentifier` properties from upload results.
 */

@Injectable({ providedIn: 'root' })
export class FileUploadValidatorService {
  public validateUploadFn(uploadHandler: FileUploadHandler): AsyncValidatorFn {
    return async (control: AbstractControl<FileInputValueModel[]>): Promise<FileValidationErrorCollection | null> => {
      if (!Array.isArray(control.value)) {
        return null;
      }

      const errorPromises: Promise<FileValidationErrorModel | void>[] = control.value
        .filter(value => !value.fileIdentifier)
        .map(value => this.uploadFileAndUpdateValue(value, uploadHandler));

      const errors: FileValidationErrorModel[] = (await Promise.all(errorPromises))
        .filter(error => !!error);

      if (errors.length) {
        return { file: errors };
      }

      return null;
    };
  }

  private async uploadFileAndUpdateValue(value: FileInputValueModel, uploadHandler: FileUploadHandler): Promise<FileValidationErrorModel | void> {
    return firstValueFrom(uploadHandler(value.file))
      .then(result => {
        value.fileIdentifier = result.fileIdentifier;
      })
      .catch((error: AppErrorModel<FileUploadErrorModel>) => {
        return {
          data: value.file,
          message: error.data?.message,
        };
      });
  }
}
